import { supabase } from "@/integrations/supabase/client";
import { format, addMonths, startOfMonth, endOfMonth, parseISO } from "date-fns";

export interface MonthlyProjection {
  month: string;
  actuals: number;
  confirmed_pipeline: number;
  weighted_pipeline: number;
  total_projection: number;
}

export interface ClientProjectionDetail {
  client_id: string;
  client_name: string;
  amount: number;
  category: 'actuals' | 'confirmed' | 'weighted';
  probability?: number; // Added for weighted pipeline
}

export interface MonthlyProjectionWithDetails {
  month: string;
  actuals: number;
  confirmed_pipeline: number;
  weighted_pipeline: number;
  total_projection: number;
  details: ClientProjectionDetail[];
}

export interface ProjectionFilters {
  client_id?: string;
  project_type?: 'adhoc' | 'subscription';
  date_from?: string;
  date_to?: string;
}

// Calculate weighted value for proposals
export const calculateWeightedValue = (amount: number | null, probability: number | null): number => {
  if (!amount || !probability) return 0;
  return Math.round((amount * probability / 100) * 100) / 100;
};

// Generate payment schedule dates for projects
export const generatePaymentSchedule = (project: any): Array<{date: string, amount: number}> => {
  const schedule = project.payment_schedule || { type: 'once_off' };
  const projectValue = Number(project.value ?? project.sold_for ?? 0);
  const payments: Array<{date: string, amount: number}> = [];
  // Debug logging for ERGO Wisdrop project
  if (project.id === '5b8e9249-64ec-4cef-864a-cdc9d857286a') {
    console.log('ERGO Wisdrop 2025-2026 - Generating payment schedule');
    console.log('ERGO Wisdrop 2025-2026 - Project:', { id: project.id, name: project.name });
    console.log('ERGO Wisdrop 2025-2026 - Schedule:', schedule);
    console.log('ERGO Wisdrop 2025-2026 - Project value:', projectValue);
  }

  switch (schedule.type) {
    case 'once_off':
      // Priority for invoicing date:
      // 1. Explicit invoicing_date in schedule (manual override)
      // 2. billing_date
      // 3. delivery_date
      // 4. delivered_date IF it's in the same year as start_date (short-term projects like Ergo Insurance Tracker)
      // 5. start_date (for subscription-type projects where delivered_date is 1 year later)
      // 6. delivered_date (final fallback)
      if (schedule.invoicing_date) {
        payments.push({ date: schedule.invoicing_date, amount: projectValue });
      } else if (project.billing_date) {
        payments.push({ date: project.billing_date, amount: projectValue });
      } else if (project.delivery_date) {
        payments.push({ date: project.delivery_date, amount: projectValue });
      } else if (project.delivered_date && project.start_date) {
        // Use delivered_date only if it's in the same calendar year as start_date
        // This handles short-term projects correctly (e.g., Ergo Insurance Tracker: Nov start, Dec delivery)
        // While keeping subscription projects on start_date (e.g., L'Oréal: Jan 2026 start, Jan 2027 delivery)
        const startYear = new Date(project.start_date).getFullYear();
        const deliveredYear = new Date(project.delivered_date).getFullYear();
        if (startYear === deliveredYear) {
          payments.push({ date: project.delivered_date, amount: projectValue });
        } else {
          payments.push({ date: project.start_date, amount: projectValue });
        }
      } else if (project.start_date) {
        payments.push({ date: project.start_date, amount: projectValue });
      } else if (project.delivered_date) {
        payments.push({ date: project.delivered_date, amount: projectValue });
      }
      break;

    case 'split':
      if (schedule.splits && Array.isArray(schedule.splits)) {
        schedule.splits.forEach((split: any) => {
          let paymentDate = '';
          
          if (split.trigger === 'at_start' && project.start_date) {
            paymentDate = project.start_date;
          } else if (split.trigger === 'at_delivery') {
            // Priority: delivered_date > delivery_date > start_date + 2 months (fallback)
            if (project.delivered_date) {
              paymentDate = project.delivered_date;
            } else if (project.delivery_date) {
              paymentDate = project.delivery_date;
            } else if (project.start_date) {
              // Fallback: estimate delivery as 2 months after start
              paymentDate = format(addMonths(parseISO(project.start_date), 2), 'yyyy-MM-dd');
            }
          } else if (split.trigger === 'custom' && split.date) {
            paymentDate = split.date;
          }
          
          if (paymentDate) {
              payments.push({
                date: paymentDate,
                amount: Math.round((projectValue * Number(split.percentage || 0) / 100) * 100) / 100
              });
          }
        });
      }
      break;

    case 'recurring':
      if (schedule.amount_per_interval && schedule.frequency) {
        // Determine start date based on start_date_type or explicit start_date
        let recurringStartDate;
        if (schedule.start_date_type === 'project_start' && project.start_date) {
          recurringStartDate = parseISO(project.start_date);
        } else if (schedule.start_date) {
          recurringStartDate = parseISO(schedule.start_date);
        }
        
        if (recurringStartDate) {
          // Determine end date - use project delivery date or explicit end date
          const endDate = schedule.end_date ? parseISO(schedule.end_date) : 
                         (project.delivered_date ? parseISO(project.delivered_date) : 
                          addMonths(recurringStartDate, 24));
           const amount = Number(schedule.amount_per_interval);
           
           let currentDate = recurringStartDate;
           let monthsToAdd = 1;
           
           if (schedule.frequency === 'quarterly') monthsToAdd = 3;
           else if (schedule.frequency === 'yearly') monthsToAdd = 12;
           
           while (currentDate <= endDate) {
             const paymentDate = format(currentDate, 'yyyy-MM-dd');
             payments.push({
               date: paymentDate,
               amount: amount
             });
             
             currentDate = addMonths(currentDate, monthsToAdd);
           }
        }
      }
      break;
  }

  return payments;
};

class SalesProjectionService {
  // Get actual sales (invoiced amounts) by month
  async getActualSales(filters: ProjectionFilters = {}) {
    try {
      console.log('Fetching actual sales...');
      const query = supabase
        .from('invoices')
        .select(`
          amount,
          issue_date,
          client_id,
          status,
          projects:project_id(type)
        `)
        .not('issue_date', 'is', null);
        // Removed status filter - all issued invoices count as actual sales regardless of payment status

      if (filters.client_id) {
        query.eq('client_id', filters.client_id);
      }

      if (filters.date_from) {
        query.gte('issue_date', filters.date_from);
      }

      if (filters.date_to) {
        query.lte('issue_date', filters.date_to);
      }

      const { data: invoices, error } = await query;

      if (error) {
        console.error('Error fetching invoices:', error);
        throw error;
      }

      console.log(`Found ${invoices?.length || 0} invoices`);

      const monthlyActuals: Record<string, number> = {};

      invoices?.forEach(invoice => {
        if (invoice.issue_date) {
          const month = format(parseISO(invoice.issue_date), 'yyyy-MM');
          
          // Apply project type filter if specified
          if (filters.project_type && invoice.projects?.type !== filters.project_type) {
            return;
          }

          monthlyActuals[month] = (monthlyActuals[month] || 0) + (invoice.amount || 0);
        }
      });

      console.log('Monthly actuals:', monthlyActuals);
      return monthlyActuals;
    } catch (error) {
      console.error('Error in getActualSales:', error);
      return {};
    }
  }

  // Get confirmed pipeline following user's specified logic:
  // 1. Get all invoices by month
  // 2. Get project payment schedules by month  
  // 3. Check for matches and exclude already invoiced amounts
  // 4. Handle proposals based on status (ignore accepted, weight others)
  async getConfirmedPipeline(filters: ProjectionFilters = {}) {
    console.log('🚨🚨🚨 NEW CONFIRMED PIPELINE LOGIC STARTING 🚨🚨🚨', new Date().toISOString());
    
    // STEP 1: Get all invoices by month to know what's already invoiced
    const { data: allInvoices } = await supabase
      .from('invoices')
      .select('id, client_id, project_id, amount, issue_date, invoice_number')
      .not('issue_date', 'is', null)
      .in('status', ['sent', 'paid', 'overdue']);

    console.log(`Step 1: Found ${allInvoices?.length || 0} issued invoices`);

    // Create invoice tracking by month
    const invoicesByMonth = new Map<string, Array<{client_id: string, project_id?: string, amount: number}>>();
    allInvoices?.forEach(invoice => {
      const month = format(parseISO(invoice.issue_date), 'yyyy-MM');
      if (!invoicesByMonth.has(month)) {
        invoicesByMonth.set(month, []);
      }
      invoicesByMonth.get(month)?.push({
        client_id: invoice.client_id,
        project_id: invoice.project_id,
        amount: Number(invoice.amount || 0)
      });
    });

    // STEP 2: Get projects and their payment schedules
    const { data: projects } = await supabase
      .from('projects')
      .select(`
        id, name, client_id, value, sold_for, payment_schedule,
        start_date, billing_date, delivery_date, delivered_date, type, status
      `)
      .in('status', ['won', 'in_progress', 'delivered', 'confirmed']);

    console.log(`Step 2: Found ${projects?.length || 0} confirmed projects`);

    // Apply client filter to projects if specified
    const filteredProjects = projects?.filter(project => 
      !filters.client_id || project.client_id === filters.client_id
    ) || [];

    // Apply project type filter if specified  
    const typeFilteredProjects = filteredProjects.filter(project =>
      !filters.project_type || project.type === filters.project_type
    );

    console.log(`Step 2: After filters: ${typeFilteredProjects.length} projects`);

    const monthlyPipeline: Record<string, number> = {};

    // Process each project's payment schedule
    typeFilteredProjects.forEach(project => {
      const payments = generatePaymentSchedule(project);
      
      if (project.id === '5b8e9249-64ec-4cef-864a-cdc9d857286a') {
        console.log(`ERGO Debug: Generated ${payments.length} payments:`, payments);
      }

      payments.forEach(payment => {
        if (!payment.date) return;

        const month = format(parseISO(payment.date), 'yyyy-MM');
        
        // Only include current month and future
        const paymentMonth = startOfMonth(parseISO(payment.date));
        const currentMonth = startOfMonth(new Date());
        if (paymentMonth < currentMonth) return;

        // Apply date range filters
        if (filters.date_from && payment.date < filters.date_from) return;
        if (filters.date_to && payment.date > filters.date_to) return;

        // STEP 3: Check if this payment matches any existing invoice
        const monthInvoices = invoicesByMonth.get(month) || [];
        let shouldExclude = false;

        for (const invoice of monthInvoices) {
          // Match by project + amount (precise for split payments)
          if (
            invoice.client_id === project.client_id &&
            invoice.project_id === project.id &&
            Math.abs(invoice.amount - payment.amount) < 0.01
          ) {
            shouldExclude = true;
            if (project.id === '5b8e9249-64ec-4cef-864a-cdc9d857286a') {
              console.log(`ERGO Debug: Excluding ${month} payment €${payment.amount} - project+amount match with invoice`);
            }
            break;
          }

          // Match by client + amount (only when invoice has no project_id)
          if (
            invoice.client_id === project.client_id &&
            !invoice.project_id &&
            Math.abs(invoice.amount - payment.amount) < 0.01
          ) {
            shouldExclude = true;
            if (project.id === '5b8e9249-64ec-4cef-864a-cdc9d857286a') {
              console.log(`ERGO Debug: Excluding ${month} payment €${payment.amount} - client+amount match (invoice without project_id)`);
            }
            break;
          }
        }

        if (!shouldExclude) {
          monthlyPipeline[month] = (monthlyPipeline[month] || 0) + payment.amount;
          if (project.id === '5b8e9249-64ec-4cef-864a-cdc9d857286a') {
            console.log(`ERGO Debug: Adding ${month} payment €${payment.amount} to pipeline`);
          }
        }
      });
    });

    // STEP 4: Handle proposals based on status
    const { data: proposals } = await supabase
      .from('proposals')
      .select('id, client_id, value, final_value, status, probability, expected_invoicing_date')
      .not('expected_invoicing_date', 'is', null);

    console.log(`Step 4: Found ${proposals?.length || 0} proposals with expected invoicing dates`);

    // Apply client filter to proposals if specified
    const filteredProposals = proposals?.filter(proposal =>
      !filters.client_id || proposal.client_id === filters.client_id
    ) || [];

    console.log(`Step 4: After client filter: ${filteredProposals.length} proposals`);

    filteredProposals.forEach(proposal => {
      const month = format(parseISO(proposal.expected_invoicing_date), 'yyyy-MM');
      const proposalValue = proposal.final_value || proposal.value || 0;

      // Apply date range filters
      if (filters.date_from && proposal.expected_invoicing_date < filters.date_from) return;
      if (filters.date_to && proposal.expected_invoicing_date > filters.date_to) return;

      // Only include current month and future
      const proposalMonth = startOfMonth(parseISO(proposal.expected_invoicing_date));
      const currentMonth = startOfMonth(new Date());
      if (proposalMonth < currentMonth) return;

      // If status is 'accepted', ignore completely (per user instructions)
      if (proposal.status === 'accepted') {
        console.log(`Ignoring accepted proposal ${proposal.id} for ${month} - not counted anywhere`);
        return;
      }

      // For non-accepted proposals (draft, pending, overdue), they go to weighted pipeline
      // This is handled in getWeightedPipeline method
    });

    console.log(`Final pipeline by month:`, monthlyPipeline);
    return monthlyPipeline;
  }

  // Get weighted pipeline from proposals (non-accepted status only)
  // Per user instructions: only proposals with status != 'accepted' go to weighted pipeline
  async getWeightedPipeline(filters: ProjectionFilters = {}) {
    const query = supabase
      .from('proposals')
      .select(`
        value,
        final_value,
        probability,
        expected_invoicing_date,
        client_id,
        status
      `)
      .not('status', 'eq', 'accepted') // Exclude accepted proposals (they're ignored completely)
      .not('expected_invoicing_date', 'is', null);

    if (filters.client_id) {
      query.eq('client_id', filters.client_id);
    }

    if (filters.date_from) {
      query.gte('expected_invoicing_date', filters.date_from);
    }

    if (filters.date_to) {
      query.lte('expected_invoicing_date', filters.date_to);
    }

    const { data: proposals, error } = await query;

    if (error) throw error;

    console.log(`Found ${proposals?.length || 0} non-accepted proposals for weighted pipeline`);

    const monthlyWeighted: Record<string, number> = {};

    proposals?.forEach(proposal => {
      if (proposal.expected_invoicing_date) {
        const month = format(parseISO(proposal.expected_invoicing_date), 'yyyy-MM');
        
        // Only include current month and future
        const proposalMonth = startOfMonth(parseISO(proposal.expected_invoicing_date));
        const currentMonth = startOfMonth(new Date());
        if (proposalMonth < currentMonth) return;

        const proposalValue = proposal.final_value || proposal.value || 0;
        const probability = proposal.probability || 0;
        
        console.log(`Processing proposal for weighted pipeline: month=${month}, value=${proposalValue}, probability=${probability}, status=${proposal.status}`);
        
        // Calculate weighted value based on probability
        const weightedAmount = calculateWeightedValue(proposalValue, probability);
        monthlyWeighted[month] = (monthlyWeighted[month] || 0) + weightedAmount;
      }
    });

    console.log('Weighted pipeline by month:', monthlyWeighted);
    return monthlyWeighted;
  }

  // Get comprehensive monthly projection data
  async getMonthlyProjections(filters: ProjectionFilters = {}): Promise<MonthlyProjection[]> {
    const [actuals, confirmed, weighted] = await Promise.all([
      this.getActualSales(filters),
      this.getConfirmedPipeline(filters),
      this.getWeightedPipeline(filters)
    ]);

    // Generate months for projection (last 12 months + next 12 months)
    const months = [];
    const startDate = addMonths(new Date(), -12);
    
    for (let i = 0; i < 24; i++) {
      const monthDate = addMonths(startDate, i);
      const monthKey = format(monthDate, 'yyyy-MM');
      
      months.push({
        month: monthKey,
        actuals: actuals[monthKey] || 0,
        confirmed_pipeline: confirmed[monthKey] || 0,
        weighted_pipeline: weighted[monthKey] || 0,
        total_projection: (actuals[monthKey] || 0) + (confirmed[monthKey] || 0) + (weighted[monthKey] || 0)
      });
    }

    return months.filter(m => 
      m.actuals > 0 || 
      m.confirmed_pipeline > 0 || 
      m.weighted_pipeline > 0 ||
      new Date(`${m.month}-01`) >= addMonths(new Date(), -6) // Always show last 6 months and future
    );
  }

  // Get detailed monthly projection data with client breakdown using database function
  async getMonthlyProjectionsWithDetails(filters: ProjectionFilters = {}): Promise<MonthlyProjectionWithDetails[]> {
    try {
      console.log('SalesProjectionService: Starting getMonthlyProjectionsWithDetails with filters:', filters);
      console.log('Using fallback method for sales projections...');
      // Use fallback method directly since it's more reliable than the database function
      const result = await this.getFallbackProjections(filters);
      console.log('SalesProjectionService: Successfully got', result?.length, 'months of data');
      return result;
    } catch (error) {
      console.error('SalesProjectionService: Error in getMonthlyProjectionsWithDetails:', error);
      throw error;
    }
  }

  // Fallback method for when the database function fails
  private async getFallbackProjections(filters: ProjectionFilters = {}): Promise<MonthlyProjectionWithDetails[]> {
    console.log('Using fallback projection method with client details...');
    
    try {
      // Get detailed data for client breakdown
      const [actualsData, confirmedData, weightedData] = await Promise.all([
        this.getDetailedActualSales(filters),
        this.getDetailedConfirmedPipeline(filters), 
        this.getDetailedWeightedPipeline(filters)
      ]);

      console.log('Detailed fallback data:', {
        actuals: actualsData.length,
        confirmed: confirmedData.length,
        weighted: weightedData.length
      });

      // Combine all months from the three datasets
      const allMonths = new Set([
        ...actualsData.map(a => a.month),
        ...confirmedData.map(c => c.month),  
        ...weightedData.map(w => w.month)
      ]);

      const monthlyData: MonthlyProjectionWithDetails[] = [];

      allMonths.forEach(month => {
        const actualsForMonth = actualsData.filter(a => a.month === month);
        const confirmedForMonth = confirmedData.filter(c => c.month === month);
        const weightedForMonth = weightedData.filter(w => w.month === month);

        // **CRITICAL FIX: Remove duplicates from confirmed pipeline**
        const filteredConfirmedForMonth = confirmedForMonth.filter(confirmed => {
          // Check if this confirmed item already exists in actuals for the same month
          const isDuplicate = actualsForMonth.some(actual => 
            actual.client_id === confirmed.client_id && 
            Math.abs(actual.amount - confirmed.amount) < 1
          );
          
          if (isDuplicate) {
            console.log(`🔴 DUPLICATE REMOVED: ${confirmed.client_name} €${confirmed.amount} from ${month} confirmed pipeline (already in actuals)`);
          } else {
            console.log(`🟢 KEEPING: ${confirmed.client_name} €${confirmed.amount} in ${month} confirmed pipeline`);
          }
          
          return !isDuplicate;
        });

        const actualsTotal = actualsForMonth.reduce((sum, item) => sum + item.amount, 0);
        const confirmedTotal = filteredConfirmedForMonth.reduce((sum, item) => sum + item.amount, 0);
        const weightedTotal = weightedForMonth.reduce((sum, item) => sum + item.amount, 0);

        const details: ClientProjectionDetail[] = [
          ...actualsForMonth.map(item => ({
            client_id: item.client_id,
            client_name: item.client_name,
            amount: item.amount,
            category: 'actuals' as const
          })),
          ...filteredConfirmedForMonth.map(item => ({
            client_id: item.client_id,
            client_name: item.client_name,
            amount: item.amount,
            category: 'confirmed' as const
          })),
          ...weightedForMonth.map(item => ({
            client_id: item.client_id,
            client_name: item.client_name,
            amount: item.amount,
            category: 'weighted' as const,
            probability: item.probability
          }))
        ];

        if (actualsTotal > 0 || confirmedTotal > 0 || weightedTotal > 0) {
          monthlyData.push({
            month,
            actuals: actualsTotal,
            confirmed_pipeline: confirmedTotal,
            weighted_pipeline: weightedTotal,
            total_projection: actualsTotal + confirmedTotal + weightedTotal,
            details
          });
        }
      });

      const result = monthlyData.sort((a, b) => a.month.localeCompare(b.month));
      console.log(`Fallback returning ${result.length} months of data with client details`);
      console.log('Sample data with details:', result.slice(0, 2));
      return result;
    } catch (fallbackError) {
      console.error('Fallback method also failed:', fallbackError);
      return [];
    }
  }

  private async getDetailedActualSales(filters: ProjectionFilters = {}): Promise<Array<{month: string, client_id: string, client_name: string, amount: number}>> {
    try {
      console.log('getDetailedActualSales: Starting query with filters:', filters);
      const query = supabase
        .from('invoices')
        .select(`
          id,
          amount,
          issue_date,
          client_id,
          status,
          clients!inner(name),
          projects:project_id(type)
        `)
        .not('issue_date', 'is', null);

      if (filters.client_id) {
        query.eq('client_id', filters.client_id);
      }

      if (filters.date_from) {
        query.gte('issue_date', filters.date_from);
      }

      if (filters.date_to) {
        query.lte('issue_date', filters.date_to);
      }

      const { data: invoices, error } = await query;

      console.log('getDetailedActualSales: Query result:', {
        error: error,
        invoicesCount: invoices?.length || 0,
        sampleInvoices: invoices?.slice(0, 3)
      });

      if (error) {
        console.error('getDetailedActualSales: Query error:', error);
        throw error;
      }

      const results = invoices?.map(invoice => {
        if (!invoice.issue_date || !invoice.clients?.name) {
          console.log('getDetailedActualSales: Skipping invoice due to missing data:', {
            invoiceId: invoice.id,
            hasIssueDate: !!invoice.issue_date,
            hasClientName: !!invoice.clients?.name
          });
          return null;
        }

        // Apply project type filter if specified
        if (filters.project_type && invoice.projects?.type !== filters.project_type) {
          console.log('getDetailedActualSales: Skipping invoice due to project type filter:', {
            invoiceId: invoice.id,
            projectType: invoice.projects?.type,
            filterType: filters.project_type
          });
          return null;
        }

        return {
          month: format(parseISO(invoice.issue_date), 'yyyy-MM'),
          client_id: invoice.client_id,
          client_name: invoice.clients.name,
          amount: invoice.amount || 0
        };
      }).filter(Boolean) || [];

      console.log('getDetailedActualSales: Final results:', {
        resultsCount: results.length,
        sampleResults: results.slice(0, 3)
      });

      return results;
    } catch (error) {
      console.error('Error in getDetailedActualSales:', error);
      return [];
    }
  }

  private async getDetailedConfirmedPipeline(filters: ProjectionFilters = {}): Promise<Array<{month: string, client_id: string, client_name: string, amount: number}>> {
    try {
      console.log('🚨🚨🚨 getDetailedConfirmedPipeline: NEW LOGIC STARTING 🚨🚨🚨', filters);
      
      // STEP 1: Get all invoices by month to know what's already invoiced
      const { data: allInvoices } = await supabase
        .from('invoices')
        .select('id, client_id, project_id, amount, issue_date, invoice_number, status')
        .not('issue_date', 'is', null);  // All invoices with issue_date, regardless of status

      console.log(`📄 Step 1: Found ${allInvoices?.length || 0} issued invoices`);
      
      if (allInvoices && allInvoices.length > 0) {
        console.log('📄 Step 1: Sample invoices statuses:', 
          allInvoices.slice(0, 5).map(inv => ({ status: inv.status, amount: inv.amount, client_id: inv.client_id }))
        );
      }

      // Create invoice tracking by month
      const invoicesByMonth = new Map<string, Array<{client_id: string, project_id?: string, amount: number}>>();
      allInvoices?.forEach(invoice => {
        const month = format(parseISO(invoice.issue_date), 'yyyy-MM');
        if (!invoicesByMonth.has(month)) {
          invoicesByMonth.set(month, []);
        }
        invoicesByMonth.get(month)?.push({
          client_id: invoice.client_id,
          project_id: invoice.project_id,
          amount: Number(invoice.amount || 0)
        });
      });

      // STEP 2: Get projects and their payment schedules (with linked proposal probability)
      const { data: projects } = await supabase
        .from('projects')
        .select(`
          id, name, client_id, value, sold_for, payment_schedule,
          start_date, billing_date, delivery_date, delivered_date, type, status,
          proposal_id,
          clients!inner(name),
          proposals:proposal_id(probability)
        `)
        .in('status', ['won', 'in_progress', 'delivered', 'confirmed']);

      console.log(`🚀 Step 2: Found ${projects?.length || 0} confirmed projects`);

      // Apply client filter to projects if specified
      const filteredProjects = projects?.filter(project => 
        !filters.client_id || project.client_id === filters.client_id
      ) || [];

      // Apply project type filter if specified  
      const typeFilteredProjects = filteredProjects.filter(project =>
        !filters.project_type || project.type === filters.project_type
      );

      console.log(`✅ Step 2: After filters: ${typeFilteredProjects.length} projects`);

      const results: Array<{month: string, client_id: string, client_name: string, amount: number, probability?: number}> = [];

      // Process each project's payment schedule
      typeFilteredProjects.forEach(project => {
        const payments = generatePaymentSchedule(project);
        
        if (project.id === '5b8e9249-64ec-4cef-864a-cdc9d857286a') {
          console.log(`🎯 ERGO Debug: Generated ${payments.length} payments:`, payments);
        }

        payments.forEach(payment => {
          if (!payment.date) return;

          const month = format(parseISO(payment.date), 'yyyy-MM');
          
          // Only include current month and future
          const paymentMonth = startOfMonth(parseISO(payment.date));
          const currentMonth = startOfMonth(new Date());
          if (paymentMonth < currentMonth) return;

          // Apply date range filters
          if (filters.date_from && payment.date < filters.date_from) return;
          if (filters.date_to && payment.date > filters.date_to) return;

          // STEP 3: Check if this payment has already been invoiced this month
          const monthInvoices = invoicesByMonth.get(month) || [];
          let shouldExclude = false;

          // Debug logs για όλους τους πελάτες
          console.log(`🔍 ${project.clients.name} Debug: Checking ${month} payment €${payment.amount}`);
          console.log(`🔍 ${project.clients.name} Debug: Month invoices count: ${monthInvoices.length}`);
          
           for (const invoice of monthInvoices) {
             console.log(`🔍 ${project.clients.name} Debug: Invoice - client: ${invoice.client_id}, amount: €${invoice.amount}, project: ${invoice.project_id}`);
             console.log(`🔍 ${project.clients.name} Debug: Payment - client: ${project.client_id}, amount: €${payment.amount}, project: ${project.id}`);

             // Precise match: project + amount (required for split payments)
             if (
               invoice.client_id === project.client_id &&
               invoice.project_id === project.id &&
               Math.abs(invoice.amount - payment.amount) < 1
             ) {
               shouldExclude = true;
               console.log(`🔴 ${project.clients.name} Debug: EXCLUDING payment - matches invoice by project+amount`);
               break;
             }

             // Fallback match: client + amount ONLY when invoice has no project_id
             if (
               invoice.client_id === project.client_id &&
               !invoice.project_id &&
               Math.abs(invoice.amount - payment.amount) < 1
             ) {
               shouldExclude = true;
               console.log(`🔴 ${project.clients.name} Debug: EXCLUDING payment - matches invoice by client+amount (invoice without project_id)`);
               break;
             }
           }

          if (!shouldExclude) {
            // Projects in confirmed status (won/in_progress/delivered/confirmed) are already confirmed
            // So we don't apply probability - they should show full amount
            results.push({
              month,
              client_id: project.client_id,
              client_name: project.clients.name,
              amount: Number(payment.amount)
            });
            console.log(`🟢 ${project.clients.name} Debug: ADDED ${month} payment €${payment.amount} to pipeline (confirmed project, full amount)`);
          } else {
            console.log(`🔴 ${project.clients.name} Debug: EXCLUDED ${month} payment €${payment.amount}`);
          }
        });
      });

      // STEP 4: Handle proposals - according to user specs, accepted proposals should be IGNORED completely
      // (They don't go to confirmed pipeline, they don't go to weighted pipeline - they are ignored)

      console.log(`📊 Final confirmed pipeline results: ${results.length} entries`);
      return results;

    } catch (error) {
      console.error('❌ Error in getDetailedConfirmedPipeline:', error);
      return [];
    }
  }

  private async getDetailedWeightedPipeline(filters: ProjectionFilters = {}): Promise<Array<{month: string, client_id: string, client_name: string, amount: number, probability: number}>> {
    try {
      console.log('getDetailedWeightedPipeline: Starting with filters:', filters);
      
      const results: Array<{month: string, client_id: string, client_name: string, amount: number, probability: number}> = [];

      // PART 1: Get draft projects with linked proposals that have probability
      const { data: draftProjects } = await supabase
        .from('projects')
        .select(`
          id, name, client_id, value, sold_for, payment_schedule,
          start_date, billing_date, delivery_date, type, status,
          proposal_id,
          clients!inner(name),
          proposals:proposal_id(probability, status)
        `)
        .eq('status', 'draft')
        .not('proposal_id', 'is', null);

      console.log('getDetailedWeightedPipeline: Found draft projects with proposals:', draftProjects?.length || 0);

      // Process draft projects using their payment_schedule with proposal probability
      draftProjects?.forEach(project => {
        const proposalData = (project as any).proposals;
        const probability = proposalData?.probability;
        
        // Only include if proposal is pending/pending_response and has valid probability
        if (!probability || probability <= 0 || probability >= 100) return;
        if (proposalData?.status === 'accepted') return; // Accepted proposals are ignored

        // Apply client filter
        if (filters.client_id && project.client_id !== filters.client_id) return;
        // Apply project type filter
        if (filters.project_type && project.type !== filters.project_type) return;

        const payments = generatePaymentSchedule(project);
        
        console.log(`🎯 Draft project ${project.name}: ${payments.length} payments with ${probability}% probability`);

        payments.forEach(payment => {
          if (!payment.date) return;

          const month = format(parseISO(payment.date), 'yyyy-MM');
          
          // Only include current month and future
          const paymentMonth = startOfMonth(parseISO(payment.date));
          const currentMonth = startOfMonth(new Date());
          if (paymentMonth < currentMonth) return;

          // Apply date range filters
          if (filters.date_from && payment.date < filters.date_from) return;
          if (filters.date_to && payment.date > filters.date_to) return;

          const weightedAmount = calculateWeightedValue(Number(payment.amount), probability);
          
          results.push({
            month,
            client_id: project.client_id,
            client_name: project.clients.name,
            amount: weightedAmount,
            probability: probability
          });
          
          console.log(`🟡 ${project.clients.name}: Added weighted payment €${payment.amount} * ${probability}% = €${weightedAmount} for ${month}`);
        });
      });

      // PART 2: Get proposals WITHOUT linked projects (standalone proposals)
      const query = supabase
        .from('proposals')
        .select(`
          id,
          value,
          final_value,
          probability,
          expected_invoicing_date,
          client_id,
          status,
          clients!inner(name)
        `)
        .in('status', ['draft', 'pending', 'pending_response'])
        .not('expected_invoicing_date', 'is', null);

      if (filters.client_id) {
        query.eq('client_id', filters.client_id);
      }

      if (filters.date_from) {
        query.gte('expected_invoicing_date', filters.date_from);
      }

      if (filters.date_to) {
        query.lte('expected_invoicing_date', filters.date_to);
      }

      const { data: proposals, error } = await query;

      if (error) {
        console.error('getDetailedWeightedPipeline: Query error:', error);
        throw error;
      }

      // Get proposal IDs that are already linked to draft projects (to avoid double counting)
      const linkedProposalIds = new Set(draftProjects?.map(p => p.proposal_id).filter(Boolean) || []);

      proposals?.forEach(proposal => {
        // Skip if this proposal is already linked to a draft project (already processed above)
        if (linkedProposalIds.has(proposal.id)) {
          console.log(`Skipping proposal ${proposal.id} - already processed via linked project`);
          return;
        }

        if (!proposal.expected_invoicing_date || !proposal.clients?.name) return;

        const month = format(parseISO(proposal.expected_invoicing_date), 'yyyy-MM');
        const proposalValue = proposal.final_value || proposal.value || 0;
        const probability = proposal.probability || 0;
        
        if (probability < 100 && probability > 0) {
          const weightedAmount = calculateWeightedValue(proposalValue, probability);
          
          results.push({
            month,
            client_id: proposal.client_id,
            client_name: proposal.clients.name,
            amount: weightedAmount,
            probability: probability
          });
        }
      });

      console.log('getDetailedWeightedPipeline: Final results:', results.length);

      return results;
    } catch (error) {
      console.error('Error in getDetailedWeightedPipeline:', error);
      return [];
    }
  }

  // Get projection summary statistics
  async getProjectionSummary(filters: ProjectionFilters = {}) {
    const projections = await this.getMonthlyProjections(filters);
    const currentMonth = format(new Date(), 'yyyy-MM');
    
    const futureProjections = projections.filter(p => p.month >= currentMonth);
    const pastActuals = projections.filter(p => p.month < currentMonth);
    
    return {
      total_pipeline_value: futureProjections.reduce((sum, p) => sum + p.confirmed_pipeline + p.weighted_pipeline, 0),
      total_confirmed_pipeline: futureProjections.reduce((sum, p) => sum + p.confirmed_pipeline, 0),
      total_weighted_pipeline: futureProjections.reduce((sum, p) => sum + p.weighted_pipeline, 0),
      ytd_actuals: pastActuals
        .filter(p => p.month.startsWith(new Date().getFullYear().toString()))
        .reduce((sum, p) => sum + p.actuals, 0),
      next_month_projection: projections.find(p => p.month === format(addMonths(new Date(), 1), 'yyyy-MM'))?.total_projection || 0
    };
  }
}

export const salesProjectionService = new SalesProjectionService();